import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toTitleCase } from "@/lib/utils";
import { ArrowLeft, Send, Loader2, CheckCircle, MessageCircle, FlaskConical, Microscope, QrCode, Paperclip, X } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-gray-100 text-gray-600",
  pending: "bg-sky-100 text-sky-700",
};
const WHATSAPP_NUMBER = "919174174174";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi")}`;

export default function ComplaintPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Top-level tab: instrument | reagent
  const [topTab, setTopTab] = useState("instrument");
  // Reagent sub-tab: new | history
  const [subTab, setSubTab] = useState("new");
  // Form state
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [reagentSegment, setReagentSegment] = useState("");
  const [reagentCode, setReagentCode] = useState("");
  const [issue, setIssue] = useState("");
  const [complaintType, setComplaintType] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showLotScanner, setShowLotScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  // Data
  const [complaints, setComplaints] = useState([]);
  const [allReagents, setAllReagents] = useState([]);
  const [reagentsLoading, setReagentsLoading] = useState(false);
  const [complaintTypes, setComplaintTypes] = useState(["Leakage", "Damage", "Quality", "RFID", "Temp not maintained"]);

  // Pull authoritative list of complaint types from backend (overrides the local fallback above)
  useEffect(() => {
    (async () => {
      try {
        const r = await API.get("/complaints/reagent-types");
        if (Array.isArray(r.data?.types) && r.data.types.length) setComplaintTypes(r.data.types);
      } catch { /* keep local fallback */ }
    })();
  }, []);

  // Build list of instruments mapped to this user (from registry)
  const userInstruments = useMemo(() => {
    const items = (user?.instruments || []).filter(i => i && (i.PRODUCT_SHORT_NAME || i.ITEM_CODE || i.SERIAL_NUMBER));
    return items.map(i => ({
      value: i.ITEM_CODE || i.PRODUCT_SHORT_NAME || i.SERIAL_NUMBER,
      label: `${toTitleCase(i.PRODUCT_SHORT_NAME || i.PRODUCT_SUB_CATEGORY || "Instrument")}${i.SERIAL_NUMBER ? ` — Serial ${i.SERIAL_NUMBER}` : ""}`,
    }));
  }, [user]);

  // Load all reagents (system-wide) once when entering Reagent tab
  const loadAllReagents = useCallback(async () => {
    setReagentsLoading(true);
    try {
      const { data } = await API.get(`/products?limit=500`);
      setAllReagents(data?.products || []);
    } catch { setAllReagents([]); } finally { setReagentsLoading(false); }
  }, []);

  useEffect(() => {
    if (topTab !== "reagent" || allReagents.length !== 0) return;
    let cancelled = false;
    (async () => { if (!cancelled) await loadAllReagents(); })();
    return () => { cancelled = true; };
  }, [topTab, allReagents.length, loadAllReagents]);

  // Segment options (reagent categories) + reagents filtered by the chosen segment.
  const reagentSegments = useMemo(() => {
    const set = new Set(allReagents.map(p => p.category).filter(c => c && c.toLowerCase() !== "test"));
    return Array.from(set).sort();
  }, [allReagents]);
  const filteredReagents = useMemo(() => (
    reagentSegment ? allReagents.filter(p => p.category === reagentSegment) : allReagents
  ), [allReagents, reagentSegment]);

  // My Complaints history
  useEffect(() => {
    if (topTab === "reagent" && subTab === "history") {
      (async () => {
        try {
          const r = await API.get("/complaints");
          setComplaints(r.data || []);
        } catch { /* ignore */ }
      })();
    }
  }, [topTab, subTab]);

  // Instrument-tab: select an instrument → straight to WhatsApp
  const onInstrumentTabSelect = (code) => {
    setSelectedInstrument(code);
    if (code) {
      window.open(WHATSAPP_LINK, "_blank", "noopener");
    }
  };

  const canSubmitReagent = reagentCode && issue.trim() && complaintType;

  const handleAttachmentChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await API.post("/complaints/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachmentUrl(data.attachment_url);
      setAttachmentName(data.filename || f.name);
    } catch (err) {
      // Surface the error inline so the user knows the upload failed.
      setAttachmentUrl(""); setAttachmentName("");
      window.alert(err.response?.data?.detail || "Could not upload attachment. Try a smaller PDF / JPG / PNG.");
    } finally { setUploadingAttachment(false); e.target.value = ""; }
  };

  const removeAttachment = () => { setAttachmentUrl(""); setAttachmentName(""); };

  // Lot Number scanner — reuses html5-qrcode (already used by the main scanner)
  const lotScannerRef = "lot-scanner-region";
  useEffect(() => {
    if (!showLotScanner) return;
    let scanner = null;
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        scanner = new Html5Qrcode(lotScannerRef);
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 140 } },
          (decoded) => {
            // Extract lot from common label formats: prefer "L: <lot> : <serial>";
            // fall back to first *-delimited segment after the product code.
            const txt = String(decoded || "").trim();
            const mLot = txt.match(/L\s*:\s*([A-Za-z0-9-]+)/i);
            if (mLot) {
              setLotNumber(mLot[1]);
            } else {
              const parts = txt.split("*");
              const seg = (parts[1] || "").trim();
              setLotNumber(seg.split(":")[0].trim() || txt);
            }
            setShowLotScanner(false);
          },
          () => {}
        );
      } catch { /* scanner init failed; ignore */ }
    })();
    return () => {
      cancelled = true;
      try { scanner && scanner.stop && scanner.stop(); } catch { /* ignore */ }
      try { scanner && scanner.clear && scanner.clear(); } catch { /* ignore */ }
    };
  }, [showLotScanner]);

  const handleSubmitReagent = async () => {
    if (!canSubmitReagent) return;
    setLoading(true);
    try {
      const { data } = await API.post("/complaints", {
        instrument_serial: `REAGENT:${reagentCode}`,
        instrument_code: "",
        reagent_code: reagentCode,
        issue: issue.trim(),
        complaint_type: complaintType,
        lot_number: lotNumber.trim(),
        attachment_url: attachmentUrl,
      });
      setSubmitted(data);
    } catch { /* surfaced via UI banner if needed */ }
    finally { setLoading(false); }
  };

  if (submitted) return (
    <div className="text-center py-12 space-y-4" data-testid="complaint-success">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
      <h2 className="text-xl font-bold text-gray-900">Complaint Registered</h2>
      <p className="text-sm text-gray-500">Ticket Number: <span className="font-bold text-[#008A99]" data-testid="ticket-number">{submitted.ticket_number || submitted.sr_number}</span></p>
      <p className="text-xs text-gray-400">Our service team will contact you shortly.</p>
      <div className="flex gap-3 justify-center">
        <button onClick={() => { setSubmitted(null); setIssue(""); setReagentCode(""); }} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold" data-testid="new-complaint-btn">New Complaint</button>
        <button onClick={() => { setSubmitted(null); setSubTab("history"); }} className="px-5 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold" data-testid="view-my-complaints-btn">View My Complaints</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="complaint-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="complaint-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-bold text-gray-900">Customer Support</h1>
      </div>

      {/* Top tabs: Instrument vs Reagent */}
      <div className="flex gap-2" data-testid="complaint-top-tabs">
        <button
          onClick={() => setTopTab("instrument")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${topTab === "instrument" ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-600"}`}
          data-testid="tab-instrument"
        >
          <Microscope className="w-4 h-4" /> Instrument
        </button>
        <button
          onClick={() => setTopTab("reagent")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${topTab === "reagent" ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-600"}`}
          data-testid="tab-reagent"
        >
          <FlaskConical className="w-4 h-4" /> Reagent
        </button>
      </div>

      {/* INSTRUMENT TAB — dropdown only; on select redirect to WhatsApp */}
      {topTab === "instrument" && (
        <div className="space-y-3" data-testid="instrument-tab-content">
          <div>
            <label className="text-xs font-semibold text-gray-500 tracking-wide">Select your instrument</label>
            {userInstruments.length === 0 ? (
              <div className="mt-2 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="instrument-empty-msg">
                <p className="text-xs text-amber-700 font-semibold">No instruments mapped to your registry yet.</p>
                <p className="text-[11px] text-amber-600 mt-0.5">Please contact admin to update your Registry ID, or chat with us on WhatsApp.</p>
              </div>
            ) : (
              <select
                value={selectedInstrument}
                onChange={e => onInstrumentTabSelect(e.target.value)}
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]"
                data-testid="instrument-tab-select"
              >
                <option value="">— Select an instrument —</option>
                {userInstruments.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
          </div>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm"
            data-testid="open-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </div>
      )}

      {/* REAGENT TAB — sub-tabs (New Complaint / My Complaints) */}
      {topTab === "reagent" && (
        <div className="space-y-4" data-testid="reagent-tab-content">
          {/* Reagent sub-tabs */}
          <div className="flex gap-2 border-b border-gray-200" data-testid="reagent-subtabs">
            <button
              onClick={() => setSubTab("new")}
              className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${subTab === "new" ? "border-[#008A99] text-[#008A99]" : "border-transparent text-gray-500"}`}
              data-testid="subtab-new"
            >New Complaint</button>
            <button
              onClick={() => setSubTab("history")}
              className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${subTab === "history" ? "border-[#008A99] text-[#008A99]" : "border-transparent text-gray-500"}`}
              data-testid="subtab-history"
            >My Complaints</button>
          </div>

          {subTab === "new" && (
            <div className="space-y-4" data-testid="new-complaint-form">
              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Segment *</label>
                <select
                  value={reagentSegment}
                  onChange={e => { setReagentSegment(e.target.value); setReagentCode(""); }}
                  disabled={reagentsLoading}
                  className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99] disabled:bg-gray-50 disabled:text-gray-400"
                  data-testid="reagent-segment-select"
                >
                  <option value="">{reagentsLoading ? "Loading segments…" : "— Select a segment —"}</option>
                  {reagentSegments.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Reagent *</label>
                <select
                  value={reagentCode}
                  onChange={e => setReagentCode(e.target.value)}
                  disabled={reagentsLoading || !reagentSegment}
                  className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99] disabled:bg-gray-50 disabled:text-gray-400"
                  data-testid="reagent-select"
                >
                  <option value="">
                    {reagentsLoading ? "Loading reagents…" : !reagentSegment ? "— Select a segment first —" : "— Select a reagent —"}
                  </option>
                  {filteredReagents.map(p => (
                    <option key={p.product_code} value={p.product_code}>{toTitleCase(p.name)} — {p.product_code}</option>
                  ))}
                </select>
                {!reagentsLoading && reagentSegment && filteredReagents.length === 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">No reagents in this segment.</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Complaint Type *</label>
                <select
                  value={complaintType}
                  onChange={e => setComplaintType(e.target.value)}
                  className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]"
                  data-testid="complaint-type-select"
                >
                  <option value="">— Select type of issue —</option>
                  {complaintTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Lot Number</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    value={lotNumber}
                    onChange={e => setLotNumber(e.target.value)}
                    placeholder="Type lot number…"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]"
                    data-testid="lot-number-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLotScanner(s => !s)}
                    className={`px-3 rounded-xl text-sm font-semibold flex items-center gap-1.5 ${showLotScanner ? "bg-red-500 text-white" : "bg-[#008A99] text-white hover:bg-[#006670]"}`}
                    data-testid="lot-scan-toggle"
                    title={showLotScanner ? "Close scanner" : "Scan lot number"}
                  >
                    {showLotScanner ? <X className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
                    {showLotScanner ? "Cancel" : "Scan"}
                  </button>
                </div>
                {showLotScanner && (
                  <div className="mt-2 rounded-xl overflow-hidden border-2 border-[#008A99] bg-black" data-testid="lot-scanner-wrap">
                    <div id="lot-scanner-region" className="w-full" />
                    <p className="text-[11px] text-white/80 bg-black/60 px-3 py-2 text-center">Point camera at the lot/QR — value fills automatically.</p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Attachment <span className="font-normal text-gray-400">(photo/PDF — optional)</span></label>
                {attachmentUrl ? (
                  <div className="mt-1 flex items-center justify-between gap-2 px-3 py-2 border border-emerald-200 bg-emerald-50 rounded-xl" data-testid="complaint-attachment-preview">
                    <span className="flex items-center gap-2 text-xs text-emerald-800 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{attachmentName || "Attached"}</span>
                    </span>
                    <button onClick={removeAttachment} type="button" className="text-red-500 hover:bg-red-100 p-1 rounded" data-testid="remove-attachment-btn"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <label className="mt-1 flex items-center justify-center gap-2 py-3 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:bg-gray-50 cursor-pointer" data-testid="complaint-attachment-label">
                    {uploadingAttachment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    {uploadingAttachment ? "Uploading…" : "Attach photo or PDF"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAttachmentChange} data-testid="complaint-attachment-input" />
                  </label>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 tracking-wide">Description *</label>
                <textarea
                  value={issue}
                  onChange={e => setIssue(e.target.value)}
                  rows={4}
                  placeholder="Describe your reagent issue in detail…"
                  className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99] resize-none"
                  data-testid="reagent-issue-input"
                />
              </div>

              <button
                onClick={handleSubmitReagent}
                disabled={loading || !canSubmitReagent}
                className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-reagent-complaint-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit Complaint</>}
              </button>
            </div>
          )}

          {subTab === "history" && (
            <div className="space-y-3" data-testid="my-complaints-list">
              {complaints.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8" data-testid="no-complaints">No complaints yet</p>
              ) : complaints.map(c => {
                const isClosed = ["resolved", "closed"].includes(String(c.status || "").toLowerCase());
                const toggleSolved = async () => {
                  const next = isClosed ? null : "resolved";
                  if (!next) return; // we only allow customer to CLOSE, not reopen
                  try {
                    await API.patch(`/complaints/${c.id}/status`, { status: next });
                    const r = await API.get("/complaints");
                    setComplaints(r.data || []);
                  } catch { /* ignore */ }
                };
                return (
                  <div key={c.id || c.sr_number} className="bg-white border rounded-xl p-4 space-y-2" data-testid={`complaint-${c.sr_number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-[#008A99]">Ticket: {c.ticket_number || c.sr_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] || STATUS_COLORS.open}`}>{c.status}</span>
                    </div>
                    {c.zoho_sr_number && (
                      <p className="text-[10px] text-[#008A99] font-mono" data-testid={`complaint-zoho-sr-${c.sr_number}`}>
                        Zoho SR #{c.zoho_sr_number}
                      </p>
                    )}
                    <p className="text-sm text-gray-700">{c.issue}</p>
                    <p className="text-xs text-gray-400">{c.reagent_code ? `Reagent: ${c.reagent_code} · ` : ""}{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</p>
                    {c.resolution && (
                      <p className="text-xs text-green-600 bg-green-50 p-2 rounded-lg" data-testid={`complaint-resolution-${c.sr_number}`}>
                        <span className="font-bold">Resolution:</span> {c.resolution}
                      </p>
                    )}
                    {(c.pqcf_status || c.pqcf_no) && (
                      <div className="flex flex-wrap gap-1.5" data-testid={`complaint-pqcf-${c.sr_number}`}>
                        {c.pqcf_status && (
                          <span className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded-lg" data-testid={`complaint-pqcf-status-${c.sr_number}`}>
                            <span className="font-bold">PQCF Status:</span> {c.pqcf_status}
                          </span>
                        )}
                        {c.pqcf_no && (
                          <span className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded-lg" data-testid={`complaint-pqcf-no-${c.sr_number}`}>
                            <span className="font-bold">PQCF No.:</span> {c.pqcf_no}
                          </span>
                        )}
                      </div>
                    )}
                    {c.customer_feedback && (
                      <p className="text-xs text-sky-700 bg-sky-50 p-2 rounded-lg" data-testid={`complaint-feedback-${c.sr_number}`}>
                        <span className="font-bold">Support team:</span> {c.customer_feedback}
                      </p>
                    )}
                    {c.closure_remark && (
                      <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg" data-testid={`complaint-closure-${c.sr_number}`}>
                        <span className="font-bold">Closure:</span> {c.closure_remark}
                      </p>
                    )}
                    {/* Customer Query Solved toggle — visible only while the ticket is still open */}
                    {!isClosed && (
                      <button
                        onClick={toggleSolved}
                        className="w-full mt-1 py-2 rounded-lg border border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-xs font-bold flex items-center justify-center gap-1.5"
                        data-testid={`complaint-solve-btn-${c.sr_number}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Query Solved — Close Complaint
                      </button>
                    )}
                    {isClosed && c.closed_by_customer && (
                      <p className="text-[10px] text-gray-400 italic">Closed by you</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
